/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';

export type TPeriodoEstoque = 'mensal' | 'anual' | 'atual';
export type TAgrupamentoEstoque = 'dia' | 'mes';

export interface ICardsEstoque {
  quantidadeprodutos: number;
  quantidadeunidades: number;
  valorestoque: number;
  produtosestoqueminimo: number;
}

export interface IGraficoEstoqueItem {
  data: string;
  entradaqtde: number;
  entradavalor: number;
  vendaqtde: number;
  vendavalor: number;
  saldoqtde: number;
}

export interface IRoscaEstoque {
  percentualentradaqtde: number;
  percentualvendaqtde: number;
  percentualentradavalor: number;
  percentualvendavalor: number;
}

export interface IProdutoValorEstoque {
  idproduto: string;
  descricao: string;
  ean: string;
  quantidade: number;
  pvenda: number;
  valorestoque: number;
}

export interface IProdutoSemMovimento extends IProdutoValorEstoque {
  ultimamovimentacao: string | null;
}

export interface IDashboardEstoqueGraficos {
  intervalo: {
    datainicial: string;
    datafinal: string;
  };
  agrupamento: TAgrupamentoEstoque;
  cards: ICardsEstoque;
  grafico: IGraficoEstoqueItem[];
  totais: {
    entradaqtde: number;
    entradavalor: number;
    vendaqtde: number;
    vendavalor: number;
    saldoqtde: number;
  };
  rosca: IRoscaEstoque;
  topValorEstoque: IProdutoValorEstoque[];
  produtosSemMovimento: IProdutoSemMovimento[];
}

export interface IProdutoEstoqueMinimo {
  idproduto: string;
  descricao: string;
  ean: string;
  quan: number;
  pvenda: number;
  estoqueminino: number;
  valorestoque: number;
}

type TComTotalCount = {
  data: IProdutoEstoqueMinimo[];
  totalCount: number;
};

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const tratarErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;

    if (axiosError.response) {
      const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
      return new Error(errorMessage);
    }

    if (axiosError.request) {
      return new Error('Nenhuma resposta do servidor');
    }

    return new Error(axiosError.message);
  }

  return new Error('Erro inesperado');
};

const getDashboardEstoqueGraficos = async (
  periodo: TPeriodoEstoque = 'atual',
  ano?: number,
  mes?: number,
): Promise<IDashboardEstoqueGraficos | Error> => {
  try {
    let urlRelativa = `/dashboardestoquegraficos?periodo=${periodo}`;

    if (periodo !== 'atual' && ano) urlRelativa += `&ano=${ano}`;
    if (periodo === 'mensal' && mes) urlRelativa += `&mes=${mes}`;

    const { data } = await Api.get(urlRelativa);

    if (data) {
      const converterItem = (item: any): IGraficoEstoqueItem => ({
        data: item.data,
        entradaqtde: Number(item.entradaqtde || 0),
        entradavalor: Number(item.entradavalor || 0),
        vendaqtde: Number(item.vendaqtde || 0),
        vendavalor: Number(item.vendavalor || 0),
        saldoqtde: Number(item.saldoqtde || 0),
      });

      const converterProduto = (item: any): IProdutoValorEstoque => ({
        idproduto: item.idproduto,
        descricao: item.descricao,
        ean: item.ean,
        quantidade: Number(item.quantidade || 0),
        pvenda: Number(item.pvenda || 0),
        valorestoque: Number(item.valorestoque || 0),
      });

      const converterSemMovimento = (item: any): IProdutoSemMovimento => ({
        ...converterProduto(item),
        ultimamovimentacao: item.ultimamovimentacao,
      });

      return {
        intervalo: data.intervalo,
        agrupamento: data.agrupamento,
        cards: {
          quantidadeprodutos: Number(data.cards?.quantidadeprodutos || 0),
          quantidadeunidades: Number(data.cards?.quantidadeunidades || 0),
          valorestoque: Number(data.cards?.valorestoque || 0),
          produtosestoqueminimo: Number(data.cards?.produtosestoqueminimo || 0),
        },
        grafico: (data.grafico || []).map(converterItem),
        totais: {
          entradaqtde: Number(data.totais?.entradaqtde || 0),
          entradavalor: Number(data.totais?.entradavalor || 0),
          vendaqtde: Number(data.totais?.vendaqtde || 0),
          vendavalor: Number(data.totais?.vendavalor || 0),
          saldoqtde: Number(data.totais?.saldoqtde || 0),
        },
        rosca: {
          percentualentradaqtde: Number(data.rosca?.percentualentradaqtde || 0),
          percentualvendaqtde: Number(data.rosca?.percentualvendaqtde || 0),
          percentualentradavalor: Number(data.rosca?.percentualentradavalor || 0),
          percentualvendavalor: Number(data.rosca?.percentualvendavalor || 0),
        },
        topValorEstoque: (data.topValorEstoque || []).map(converterProduto),
        produtosSemMovimento: (data.produtosSemMovimento || []).map(converterSemMovimento),
      };
    }

    return new Error('Erro ao listar os registros.');
  } catch (error) {
    return tratarErro(error);
  }
};

const getEstoqueMinimo = async (page = 1, limit = 10): Promise<TComTotalCount | Error> => {
  try {
    const urlRelativa = `/dashboardestoqueminimo?page=${page}&limit=${limit}`;
    const { data, headers } = await Api.get(urlRelativa);

    if (data) {
      return {
        data: (data as any[]).map((item) => ({
          idproduto: item.idproduto,
          descricao: item.descricao,
          ean: item.ean,
          quan: Number(item.quan || 0),
          pvenda: Number(item.pvenda || 0),
          estoqueminino: Number(item.estoqueminino || 0),
          valorestoque: Number(item.valorestoque || 0),
        })),
        totalCount: Number(headers['x-total-count'] || 0),
      };
    }

    return new Error('Erro ao listar os registros.');
  } catch (error) {
    return tratarErro(error);
  }
};

export const GraficosEstoqueService = {
  getDashboardEstoqueGraficos,
  getEstoqueMinimo,
};
